import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import {
  CHILD_MEAL_BANDS,
  CHILD_MEAL_BAND_LABELS_FR,
  RATE_SEASONS,
  RATE_SEASON_LABELS_FR,
  fiscalYearRange,
  proposePeriods,
  seedChildMealsDirect,
  validateCalendar,
  type CalendarPeriod,
  type GenerationReport,
  type RatePlan,
  type RateProfile,
  type RateSeason,
  type RoomType,
  type StoredRateProfile,
  type StoredSeasonCalendar,
  type ZoneHoliday,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapabilities, useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';
import { createdElsewhereFr, useAskEtablissement } from './CreateTarget.js';
import { SeasonTimeline } from './SeasonTimeline.js';
import { sortByRoomCategory } from './logic.js';
import { parseProfileImport } from './profileImport.js';
import {
  dayMatrix,
  fmtCents,
  fmtPct,
  parseCents,
  parseNights,
  parsePct,
  periodDateProblem,
} from './tarifsDay.js';

// « Tarifs » — TWO objects, on two panes, because they are two things.
//
// The operator, 2026-08-24: « the tarifs tab is mixing 2 concepts into 1 — the
// seasonal breakup with date and possible base price for the double room, and
// the rate profile which allows all rooms / rate plans to be computed from the
// base price. Both are interconnected and make it unduly complicated. »
//
//   Saisons  — WHEN, and what the Double Standard costs then. Re-cut yearly
//              from the school-holiday zones; its BASE moves all year, set
//              against competing hotels and expected occupancy.
//   Profil   — HOW that base becomes every other price. Near-static.
//
// And the view that was the point of the whole request:
//
//   Tarif du jour — one date, every plan across every channel, OTA and direct.
//
// ── The screen writes rates only through « Générer » ───────────────────────
//
// Which goes to `pms_generate_rates` (0119), never to the hand path: the RPC
// refuses to touch a day marked `hand_adjusted` (0117) and reports how many it
// skipped. Hand editing still lives in « Inventaire », which is the master.

const PANES = [
  { key: 'saisons', label: 'Saisons' },
  { key: 'profil', label: 'Profil tarifaire' },
  { key: 'jour', label: 'Tarif du jour' },
] as const;
type Pane = (typeof PANES)[number]['key'];

/** A blank profile. Every number 0 and every category absent: an unset
 *  parameter must LOOK unset rather than inherit last year's by accident. */
function emptyProfile(name: string): RateProfile {
  return {
    name,
    roomTypes: {},
    mealsBooking: { breakfast: 0, dinner: 0 },
    mealsDirect: { breakfast: 0, dinner: 0 },
    lengthOfStayTiers: [],
    minStayBySeason: { basse: 1, moyenne: 1, haute: 1, tresHaute: 1 },
  };
}

export function Tarifs() {
  const pms = usePms();
  // Wait for capabilities before reading: until the first fetch settles the
  // actor is ANONYMOUS and every call is refused, with nothing retrying — the
  // lesson « Inventaire » learned by reloading the page.
  const { loading: capsLoading } = useCapabilities();
  const canEdit = useCapability(CAPABILITIES.PMS_RATE_WRITE);

  const [pane, setPane] = useState<Pane>('saisons');
  const [years, setYears] = useState<{ id: number; year: number; note: string | null }[]>([]);
  const [calendarId, setCalendarId] = useState<number | null>(null);
  const [calendar, setCalendar] = useState<StoredSeasonCalendar | null>(null);
  const [profiles, setProfiles] = useState<StoredRateProfile[]>([]);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [draft, setDraft] = useState<RateProfile | null>(null);
  const [plans, setPlans] = useState<RatePlan[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (capsLoading) return;
    setErr('');
    const [cs, ps, pl, rt] = await Promise.all([
      pms.seasons.calendars(),
      pms.seasons.profiles(),
      pms.reference.ratePlans(),
      pms.reference.roomTypes(),
    ]);
    const bad = [cs, ps, pl, rt].find((r) => !r.ok);
    if (bad && !bad.ok) {
      // Loudly rather than as an empty year: an empty calendar and a calendar
      // that failed to load look identical, and one of them means « nothing is
      // priced ».
      setErr(bad.error.message);
      return;
    }
    if (cs.ok) {
      setYears(cs.value);
      setCalendarId((c) => c ?? cs.value[0]?.id ?? null);
    }
    if (ps.ok) {
      setProfiles(ps.value);
      setProfileId((c) => c ?? ps.value[0]?.id ?? null);
    }
    if (pl.ok) setPlans(pl.value);
    if (rt.ok) setRoomTypes(sortByRoomCategory(rt.value));
  }, [pms, capsLoading]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadCalendar = useCallback(async () => {
    if (calendarId === null) {
      setCalendar(null);
      return;
    }
    const r = await pms.seasons.calendar(calendarId);
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    setCalendar(r.value);
  }, [pms, calendarId]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === profileId) ?? null,
    [profiles, profileId],
  );

  // Reset the draft only when a DIFFERENT profile is selected or its status
  // changes. Keying on the object would fire on every reload of the list, and
  // that reload would throw away unsaved edits the operator is still making.
  useEffect(() => {
    setDraft(selectedProfile?.status === 'draft' ? selectedProfile.parameters : null);
  }, [selectedProfile?.id, selectedProfile?.status]);

  /** What the previews read — the UNSAVED draft when there is one, because the
   *  point of typing a number is to see what it does before committing it. */
  const liveProfile = draft ?? selectedProfile?.parameters ?? null;

  const run = async (label: string, fn: () => Promise<{ ok: boolean; error?: { message: string } }>) => {
    setBusy(true);
    setErr('');
    setNotice('');
    const r = await fn();
    setBusy(false);
    if (!r.ok) {
      setErr(r.error?.message ?? 'Échec.');
      return false;
    }
    setNotice(label);
    await load();
    await loadCalendar();
    return true;
  };

  if (capsLoading) return <p className="pms-empty">Chargement…</p>;

  return (
    <section className="pms">
      <div className="pms-head">
        <h2 className="section-title">Tarifs</h2>
      </div>
      <p className="pms-hint">
        Deux objets distincts : <strong>les saisons</strong> (quand, et le tarif de base de la Chambre Double
        Standard) et <strong>le profil tarifaire</strong> (comment tous les autres tarifs en découlent). La
        grille se génère à partir des deux — <em>« Inventaire »</em> reste le maître pour toute retouche à la
        main.
      </p>

      {err !== '' && <p className="pms-warn">{err}</p>}
      {notice !== '' && <p className="pms-hint">{notice}</p>}

      <div className="pms-chips">
        {PANES.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`pms-chip${pane === p.key ? ' is-active' : ''}`}
            onClick={() => setPane(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {pane === 'saisons' && (
        <SaisonsPane
          years={years}
          calendarId={calendarId}
          calendar={calendar}
          canEdit={canEdit}
          busy={busy}
          onSelect={setCalendarId}
          onRun={run}
          pms={pms}
        />
      )}

      {pane === 'profil' && (
        <ProfilPane
          profiles={profiles}
          selected={selectedProfile}
          draft={draft}
          roomTypes={roomTypes}
          calendar={calendar}
          calendarId={calendarId}
          years={years}
          onSelectCalendar={setCalendarId}
          canEdit={canEdit}
          busy={busy}
          onSelect={setProfileId}
          onDraft={setDraft}
          onRun={run}
          pms={pms}
        />
      )}

      {pane === 'jour' && (
        <JourPane
          calendar={calendar}
          profile={liveProfile}
          plans={plans}
          roomTypes={roomTypes}
          calendarId={calendarId}
          profileId={profileId}
          years={years}
          onSelectCalendar={setCalendarId}
          profiles={profiles}
          onSelectProfile={setProfileId}
          canEdit={canEdit}
          busy={busy}
          onRun={run}
          pms={pms}
        />
      )}
    </section>
  );
}

// ── Saisons ──────────────────────────────────────────────────────────────────

type Pms = ReturnType<typeof usePms>;
type Runner = (label: string, fn: () => Promise<{ ok: boolean; error?: { message: string } }>) => Promise<boolean>;

type YearRow = { id: number; year: number; note: string | null };

function SaisonsPane({
  years,
  calendarId,
  calendar,
  canEdit,
  busy,
  onSelect,
  onRun,
  pms,
}: {
  years: { id: number; year: number; note: string | null }[];
  calendarId: number | null;
  calendar: StoredSeasonCalendar | null;
  canEdit: boolean;
  busy: boolean;
  onSelect: (id: number | null) => void;
  onRun: Runner;
  pms: Pms;
}) {
  // U2: a new year belongs to no existing row, so its établissement is asked.
  const { ask, picker } = useAskEtablissement();
  const { current } = usePmsScope();
  const problems = useMemo(
    () =>
      calendar
        ? validateCalendar({ year: calendar.year, periods: calendar.periods, closures: calendar.closures })
        : [],
    [calendar],
  );

  const createYear = async () => {
    const target = await ask('Nouvel exercice');
    if (target === null) return;
    const raw = window.prompt(
      'Exercice à créer (année de FIN — « 2027 » couvre le 1ᵉʳ décembre 2026 au 30 novembre 2027) :',
      String(new Date().getFullYear() + 1),
    );
    const year = Number(raw);
    if (!Number.isInteger(year) || year < 2020 || year > 2100) return;
    const elsewhere = createdElsewhereFr(target, current);
    await onRun(elsewhere ? `Exercice créé. ${elsewhere}` : 'Exercice créé.', () =>
      target.services.seasons.createCalendar(year, null),
    );
  };

  /** Fill a year from the zones. The holiday dates are the operator's to
   *  supply — Éducation nationale publishes them annually and nothing here can
   *  derive them — so they are asked for, one line per stretch. */
  const proposeFromZones = async () => {
    if (calendar === null) return;
    const raw = window.prompt(
      'Vacances scolaires zones A/B, une par ligne :\n' +
        'AAAA-MM-JJ  AAAA-MM-JJ  Libellé\n\n' +
        'Les saisons en sont déduites ; vous corrigez ensuite. ' +
        'Noël et août sont proposés en Très Haute — c’est un choix commercial, pas une règle.',
      [
        `${calendar.year - 1}-12-20\t${calendar.year}-01-05\tVacances de Noël / Nouvel An`,
        `${calendar.year}-02-07\t${calendar.year}-03-02\tVacances d'hiver`,
        `${calendar.year}-07-04\t${calendar.year}-08-31\tVacances d'été`,
      ].join('\n'),
    );
    if (raw === null) return;

    const holidays: ZoneHoliday[] = [];
    for (const line of raw.split('\n')) {
      const parts = line.trim().split(/\s+/);
      if (parts.length < 2) continue;
      const [from, to, ...rest] = parts;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(from!) || !/^\d{4}-\d{2}-\d{2}$/.test(to!)) continue;
      holidays.push({ from: from!, to: to!, label: rest.join(' ') || 'Vacances scolaires', zones: 'A/B' });
    }
    if (holidays.length === 0) return;

    const proposed = proposePeriods({ year: calendar.year, holidays });
    if (
      !window.confirm(
        `${proposed.length} période(s) proposée(s) pour l’exercice ${calendar.year}.\n\n` +
          proposed.map((p) => `${p.from} → ${p.to}  ${RATE_SEASON_LABELS_FR[p.season]}`).join('\n') +
          '\n\nLes ajouter ? Les tarifs de base restent à saisir.',
      )
    )
      return;
    await onRun('Périodes ajoutées.', () => pms.seasons.addPeriods(calendar.id, proposed));
  };

  if (calendarId === null || calendar === null) {
    return (
      <>
        <div className="pms-toolbar">
          {canEdit && (
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void createYear()}>
              Nouvel exercice
            </button>
          )}
        </div>
        <p className="pms-empty">
          Aucun calendrier de saisons. Créez un exercice (1ᵉʳ décembre → 30 novembre), puis déduisez les
          périodes des vacances scolaires.
        </p>
      </>
    );
  }

  const { from, to } = fiscalYearRange(calendar.year);

  return (
    <>
      {picker}
      <div className="pms-toolbar">
        <label className="pms-field">
          <span>Exercice</span>
          <select value={calendarId} onChange={(e) => onSelect(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.year} ({y.year - 1}-12-01 → {y.year}-11-30)
              </option>
            ))}
          </select>
        </label>
        {canEdit && (
          <>
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void createYear()}>
              Nouvel exercice
            </button>
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void proposeFromZones()}>
              Déduire des vacances scolaires
            </button>
          </>
        )}
      </div>
      <p className="pms-sub">
        Du {from} au {to}. Le tarif de base est celui de la <strong>Chambre Double Standard</strong>, nuit
        seule — tout le reste en découle par le profil.
      </p>

      {problems.length > 0 && (
        <div className="pms-warn">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <SeasonTimeline calendar={calendar} />

      <div className="pms-table-wrap">
        <table className="pms-table">
          <thead>
            <tr>
              <th>Du</th>
              <th>Au</th>
              <th>Description</th>
              <th>Zones</th>
              <th>Saison</th>
              <th>Tarif de base</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {calendar.periods.map((p) => (
              <PeriodRow
                key={p.id}
                period={p}
                fiscal={{ from, to }}
                otherStarts={calendar.periods.filter((x) => x.id !== p.id).map((x) => x.from)}
                canEdit={canEdit}
                busy={busy}
                onRun={onRun}
                pms={pms}
              />
            ))}
          </tbody>
        </table>
      </div>
      {calendar.periods.length === 0 && (
        <p className="pms-empty">Aucune période. Déduisez-les des vacances scolaires, puis ajustez.</p>
      )}
      {canEdit && (
        <AddPeriodForm calendar={calendar} fiscal={{ from, to }} busy={busy} onRun={onRun} pms={pms} />
      )}

      <h4>Fermetures</h4>
      <p className="pms-sub">
        Indépendantes des saisons : une période reste tarifée pendant une fermeture, et la grille générée la
        met en arrêt de vente.
      </p>
      <div className="pms-table-wrap">
        <table className="pms-table">
          <thead>
            <tr>
              <th>Du</th>
              <th>Au</th>
              <th>Motif</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {calendar.closures.map((c) => (
              <tr key={c.id}>
                <td>{c.from}</td>
                <td>{c.to}</td>
                <td>{c.reason}</td>
                <td>
                  {canEdit && (
                    <button
                      type="button"
                      className="pms-mini"
                      disabled={busy}
                      onClick={() => void onRun('Fermeture retirée.', () => pms.seasons.deleteClosure(c.id))}
                    >
                      Retirer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <button
          type="button"
          className="pms-mini"
          disabled={busy}
          onClick={() => {
            const f = window.prompt('Fermeture — début (AAAA-MM-JJ) :');
            if (!f) return;
            const t = window.prompt('Fermeture — fin (AAAA-MM-JJ) :', f);
            if (!t) return;
            const reason = window.prompt('Motif :', 'Fermeture saisonnière') ?? '';
            void onRun('Fermeture ajoutée.', () => pms.seasons.addClosure(calendar.id, f, t, reason));
          }}
        >
          Ajouter une fermeture
        </button>
      )}
    </>
  );
}

function PeriodRow({
  period,
  fiscal,
  otherStarts,
  canEdit,
  busy,
  onRun,
  pms,
}: {
  period: CalendarPeriod;
  fiscal: { from: string; to: string };
  otherStarts: string[];
  canEdit: boolean;
  busy: boolean;
  onRun: Runner;
  pms: Pms;
}) {
  /** Save a start or an end. The pair is checked together — moving a start past
   *  the end is only wrong against the other date — and a refusal puts the
   *  field back, so the screen never shows a date that was not stored. */
  const saveDate = (field: 'from' | 'to', input: HTMLInputElement) => {
    const next = { from: period.from, to: period.to, [field]: input.value };
    if (input.value === period[field]) return;
    const problem = periodDateProblem(next, fiscal, otherStarts);
    if (problem !== null) {
      input.value = period[field];
      window.alert(problem);
      return;
    }
    void onRun(field === 'from' ? 'Début modifié.' : 'Fin modifiée.', () =>
      pms.seasons.updatePeriod(period.id, { [field]: input.value }),
    );
  };

  return (
    <tr>
      <td>
        {canEdit ? (
          <input
            type="date"
            aria-label={`Début — ${period.description}`}
            defaultValue={period.from}
            min={fiscal.from}
            max={fiscal.to}
            disabled={busy}
            onBlur={(e) => saveDate('from', e.target)}
          />
        ) : (
          period.from
        )}
      </td>
      <td>
        {canEdit ? (
          <input
            type="date"
            aria-label={`Fin — ${period.description}`}
            defaultValue={period.to}
            min={fiscal.from}
            max={fiscal.to}
            disabled={busy}
            onBlur={(e) => saveDate('to', e.target)}
          />
        ) : (
          period.to
        )}
      </td>
      <td>{period.description}</td>
      <td>{period.zones ?? '—'}</td>
      <td>
        {canEdit ? (
          <select
            value={period.season}
            disabled={busy}
            onChange={(e) =>
              void onRun('Saison modifiée.', () =>
                pms.seasons.updatePeriod(period.id, { season: e.target.value as RateSeason }),
              )
            }
          >
            {RATE_SEASONS.map((s) => (
              <option key={s} value={s}>
                {RATE_SEASON_LABELS_FR[s]}
              </option>
            ))}
          </select>
        ) : (
          RATE_SEASON_LABELS_FR[period.season]
        )}
      </td>
      <td className="pms-num">
        {canEdit ? (
          <input
            type="text"
            inputMode="decimal"
            defaultValue={fmtCents(period.baseCents)}
            disabled={busy}
            // The number the operator turns most often. Every change is logged
            // by the database (0118), so the history survives without the
            // calendar having to be frozen.
            onBlur={(e) => {
              const c = parseCents(e.target.value);
              if (c === null || c === period.baseCents) {
                e.target.value = fmtCents(period.baseCents);
                return;
              }
              void onRun('Tarif de base modifié.', () =>
                pms.seasons.updatePeriod(period.id, { baseCents: c }),
              );
            }}
          />
        ) : (
          `${fmtCents(period.baseCents)} €`
        )}
      </td>
      <td>
        {canEdit && (
          <button
            type="button"
            className="pms-mini"
            disabled={busy}
            onClick={() => void onRun('Période retirée.', () => pms.seasons.deletePeriod(period.id))}
          >
            Retirer
          </button>
        )}
      </td>
    </tr>
  );
}

// Insert a period. Overlap is allowed on purpose (a later start wins, and the
// warning above names it), so an event week can be layered over a season without
// cutting the season in two.
function AddPeriodForm({
  calendar,
  fiscal,
  busy,
  onRun,
  pms,
}: {
  calendar: StoredSeasonCalendar;
  fiscal: { from: string; to: string };
  busy: boolean;
  onRun: Runner;
  pms: Pms;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [description, setDescription] = useState('');
  const [season, setSeason] = useState<RateSeason>('basse');
  const [base, setBase] = useState('');
  const [problem, setProblem] = useState('');

  if (!open) {
    return (
      <button type="button" className="pms-mini" disabled={busy} onClick={() => setOpen(true)}>
        Ajouter une période
      </button>
    );
  }

  const submit = async () => {
    const cents = parseCents(base);
    const bad =
      periodDateProblem(
        { from, to },
        fiscal,
        calendar.periods.map((p) => p.from),
      ) ?? (description.trim() === '' ? 'Un libellé est nécessaire.' : null) ??
      (cents === null || cents <= 0 ? 'Le tarif de base est nécessaire.' : null);
    if (bad !== null || cents === null) {
      setProblem(bad ?? '');
      return;
    }
    setProblem('');
    const done = await onRun('Période ajoutée.', () =>
      pms.seasons.addPeriods(calendar.id, [
        { from, to, description: description.trim(), zones: null, season, baseCents: cents },
      ]),
    );
    if (done) {
      setOpen(false);
      setFrom('');
      setTo('');
      setDescription('');
      setBase('');
    }
  };

  return (
    <div className="pms-form">
      <h4>Ajouter une période</h4>
      <p className="pms-sub">
        Une période qui en recouvre une autre la remplace sur les jours communs : utile pour un événement
        (Tour de France) posé sur une saison.
      </p>
      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Du</span>
          <input type="date" value={from} min={fiscal.from} max={fiscal.to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="pms-field">
          <span>Au</span>
          <input type="date" value={to} min={fiscal.from} max={fiscal.to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="pms-field">
          <span>Description</span>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="pms-field">
          <span>Saison</span>
          <select value={season} onChange={(e) => setSeason(e.target.value as RateSeason)}>
            {RATE_SEASONS.map((s) => (
              <option key={s} value={s}>
                {RATE_SEASON_LABELS_FR[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-field">
          <span>Tarif de base</span>
          <input type="text" inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} />
        </label>
      </div>
      {problem !== '' && <p className="pms-warn">{problem}</p>}
      <div className="pms-form-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void submit()}>
          Ajouter
        </button>
        <button type="button" className="pms-mini" disabled={busy} onClick={() => setOpen(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}

// ── Profil tarifaire ─────────────────────────────────────────────────────────

function ProfilPane({
  profiles,
  selected,
  draft,
  roomTypes,
  calendar,
  calendarId,
  years,
  onSelectCalendar,
  canEdit,
  busy,
  onSelect,
  onDraft,
  onRun,
  pms,
}: {
  profiles: StoredRateProfile[];
  selected: StoredRateProfile | null;
  draft: RateProfile | null;
  roomTypes: RoomType[];
  calendar: StoredSeasonCalendar | null;
  calendarId: number | null;
  years: YearRow[];
  onSelectCalendar: (id: number | null) => void;
  canEdit: boolean;
  busy: boolean;
  onSelect: (id: number | null) => void;
  onDraft: (p: RateProfile | null) => void;
  onRun: Runner;
  pms: Pms;
}) {
  const edit = (fn: (p: RateProfile) => RateProfile) => onDraft(draft === null ? null : fn(draft));
  // U2: a new profile belongs to no existing row, so its établissement is asked.
  const { ask, picker } = useAskEtablissement();
  const { current } = usePmsScope();
  const fileInput = useRef<HTMLInputElement>(null);

  const create = async (from: RateProfile | null) => {
    const target = await ask('Nouveau profil tarifaire');
    if (target === null) return;
    const name = window.prompt('Nom du profil tarifaire :', from ? `${from.name}-copie` : '2026');
    if (name === null || name.trim() === '') return;
    const base = from ? { ...from, name: name.trim() } : emptyProfile(name.trim());
    const elsewhere = createdElsewhereFr(target, current);
    await onRun(elsewhere ? `Profil créé. ${elsewhere}` : 'Profil créé.', () =>
      target.services.seasons.createProfileDraft(base, null),
    );
  };

  // Import: the file is validated in full before anything is written. A bad
  // file reports the first field at fault and leaves the screen untouched.
  const importFile = async (file: File) => {
    const parsed = parseProfileImport(await file.text());
    if (!parsed.ok) {
      window.alert(`Import impossible : ${parsed.error}`);
      return;
    }
    const target = await ask('Importer un profil tarifaire');
    if (target === null) return;
    const elsewhere = createdElsewhereFr(target, current);
    // Select the imported profile once it exists: « Générer » needs a profile
    // selected, and the one selected before the import is not this one — but
    // only when it landed in the établissement on screen, where it is listed.
    let createdId: number | null = null;
    const done = await onRun(elsewhere ? `Profil importé. ${elsewhere}` : 'Profil importé.', async () => {
      const r = await target.services.seasons.createProfileDraft(parsed.profile, parsed.note);
      if (r.ok) createdId = r.value;
      return r;
    });
    if (done && createdId !== null && !elsewhere) onSelect(createdId);
  };

  return (
    <>
      {picker}
      <div className="pms-toolbar">
        <label className="pms-field">
          <span>Profil</span>
          <select
            value={selected?.id ?? ''}
            onChange={(e) => onSelect(e.target.value === '' ? null : Number(e.target.value))}
          >
            {profiles.length === 0 && <option value="">Aucun</option>}
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.parameters.name} — {p.status}
              </option>
            ))}
          </select>
        </label>
        {canEdit && (
          <>
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void create(null)}>
              Nouveau profil
            </button>
            {selected && (
              <button
                type="button"
                className="auth-btn"
                disabled={busy}
                onClick={() => void create(selected.parameters)}
              >
                Dupliquer
              </button>
            )}
            <button type="button" className="auth-btn" disabled={busy} onClick={() => fileInput.current?.click()}>
              Importer (JSON)
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void importFile(file);
              }}
            />
          </>
        )}
      </div>

      {selected === null ? (
        <p className="pms-empty">
          Aucun profil tarifaire. Créez-en un pour saisir les suppléments de catégorie, les repas et les
          remises de séjour.
        </p>
      ) : draft === null ? (
        <>
        <p className="pms-hint">
          Profil {selected.status === 'published' ? 'publié — figé' : 'archivé'}. Pour corriger, dupliquez-le.
          {selected.status === 'published' && canEdit && (
            <>
              {' '}
              <button
                type="button"
                className="pms-mini"
                disabled={busy}
                onClick={() => void onRun('Profil archivé.', () => pms.seasons.archiveProfile(selected.id))}
              >
                Archiver
              </button>
            </>
          )}
        </p>
          <ProfileReadOnly parameters={selected.parameters} roomTypes={roomTypes} />
        </>
      ) : (
        <div className="pms-form">
          <h4>Suppléments de catégorie</h4>
          <p className="pms-sub">
            Par saison — en Très Haute saison, seules les catégories supérieures sont majorées. Le tarif de
            base, lui, appartient à la période.
          </p>
          <div className="pms-table-wrap">
            <table className="pms-table">
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Couchages</th>
                  {RATE_SEASONS.map((s) => (
                    <th key={s}>{RATE_SEASON_LABELS_FR[s]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roomTypes.map((rt) => (
                  <tr key={rt.id}>
                    <td>{rt.labelFr}</td>
                    <td className="pms-num">{rt.maxOccupancy}</td>
                    {RATE_SEASONS.map((s) => (
                      <td key={s} className="pms-num">
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="—"
                          defaultValue={
                            draft.roomTypes[rt.id] ? fmtCents(draft.roomTypes[rt.id]!.markup[s]) : ''
                          }
                          onBlur={(e) => {
                            const c = parseCents(e.target.value);
                            if (c === null) return;
                            edit((p) => {
                              const existing = p.roomTypes[rt.id] ?? {
                                markup: { basse: 0, moyenne: 0, haute: 0, tresHaute: 0 },
                                minPosted: null,
                              };
                              return {
                                ...p,
                                roomTypes: {
                                  ...p.roomTypes,
                                  [rt.id]: { ...existing, markup: { ...existing.markup, [s]: c } },
                                },
                              };
                            });
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4>Séjour minimum par saison</h4>
          <div className="pms-form-grid">
            {RATE_SEASONS.map((s) => (
              <label key={s} className="pms-field">
                <span>{RATE_SEASON_LABELS_FR[s]}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  defaultValue={String(draft.minStayBySeason[s])}
                  onBlur={(e) => {
                    const n = parseNights(e.target.value);
                    if (n === null) {
                      e.target.value = String(draft.minStayBySeason[s]);
                      return;
                    }
                    edit((p) => ({ ...p, minStayBySeason: { ...p.minStayBySeason, [s]: n } }));
                  }}
                />
              </label>
            ))}
          </div>

          <h4>Repas</h4>
          <p className="pms-sub">
            Deux tarifs distincts, et c’est voulu : le premier construit la grille OTA, le second sert au devis
            en direct.
          </p>
          <div className="pms-form-grid">
            {(
              [
                ['mealsBooking', 'breakfast', 'Petit-déjeuner — OTA'],
                ['mealsBooking', 'dinner', 'Dîner — OTA'],
                ['mealsDirect', 'breakfast', 'Petit-déjeuner — direct'],
                ['mealsDirect', 'dinner', 'Dîner — direct'],
              ] as const
            ).map(([pair, field, label]) => (
              <label key={`${pair}-${field}`} className="pms-field">
                <span>{label}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  defaultValue={fmtCents(draft[pair][field])}
                  onBlur={(e) => {
                    const c = parseCents(e.target.value);
                    if (c === null) return;
                    edit((p) => ({ ...p, [pair]: { ...p[pair], [field]: c } }));
                  }}
                />
              </label>
            ))}
          </div>

          <h4>Repas enfants — direct uniquement</h4>
          <p className="pms-sub">
            Par tranche d’âge, pour le prix direct (téléphone, site) : les OTA gardent le prix adulte par
            personne. 13-17 ans mange au prix adulte, un bébé gratuitement. À la publication, ces prix sont
            écrits dans les prestations enfants du catalogue.
          </p>
          {draft.childMealsDirect == null ? (
            <p className="pms-sub">
              Non définis : un enfant paie ses repas au prix adulte direct.{' '}
              <button
                type="button"
                className="pms-mini"
                onClick={() => edit((p) => ({ ...p, childMealsDirect: seedChildMealsDirect(p.mealsDirect) }))}
              >
                Définir les prix enfants
              </button>
            </p>
          ) : (
            <div className="pms-table-wrap">
              <table className="pms-table">
                <thead>
                  <tr>
                    <th>Tranche</th>
                    <th>Petit-déjeuner</th>
                    <th>Dîner</th>
                  </tr>
                </thead>
                <tbody>
                  {CHILD_MEAL_BANDS.map((band) => (
                    <tr key={band}>
                      <td>{CHILD_MEAL_BAND_LABELS_FR[band]}</td>
                      {(['breakfast', 'dinner'] as const).map((meal) => (
                        <td key={meal} className="pms-num">
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`${meal === 'breakfast' ? 'Petit-déjeuner' : 'Dîner'} ${CHILD_MEAL_BAND_LABELS_FR[band]}`}
                            defaultValue={fmtCents(draft.childMealsDirect![band][meal])}
                            onBlur={(e) => {
                              const c = parseCents(e.target.value);
                              if (c === null) return;
                              edit((p) =>
                                p.childMealsDirect == null
                                  ? p
                                  : {
                                      ...p,
                                      childMealsDirect: {
                                        ...p.childMealsDirect,
                                        [band]: { ...p.childMealsDirect[band], [meal]: c },
                                      },
                                    },
                              );
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>Remises par durée de séjour — direct uniquement</h4>
          <p className="pms-sub">
            Les OTA n’appliquent aucune remise de durée. Deux pourcentages indépendants par palier.
          </p>
          <div className="pms-table-wrap">
            <table className="pms-table">
              <thead>
                <tr>
                  <th>À partir de (nuits)</th>
                  <th>Remise séjour</th>
                  <th>Remise repas</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.lengthOfStayTiers.map((t, i) => (
                  <tr key={`${t.minNights}-${i}`}>
                    <td className="pms-num">
                      <input
                        type="text"
                        inputMode="numeric"
                        defaultValue={String(t.minNights)}
                        onBlur={(e) => {
                          const n = parseNights(e.target.value);
                          if (n === null) return;
                          edit((p) => ({
                            ...p,
                            lengthOfStayTiers: p.lengthOfStayTiers
                              .map((x, j) => (j === i ? { ...x, minNights: n } : x))
                              .sort((a, b) => a.minNights - b.minNights),
                          }));
                        }}
                      />
                    </td>
                    {(['discountStay', 'discountMeals'] as const).map((k) => (
                      <td key={k} className="pms-num">
                        <input
                          type="text"
                          inputMode="decimal"
                          defaultValue={fmtPct(t[k])}
                          onBlur={(e) => {
                            const v = parsePct(e.target.value);
                            if (v === null) {
                              e.target.value = fmtPct(t[k]);
                              return;
                            }
                            edit((p) => ({
                              ...p,
                              lengthOfStayTiers: p.lengthOfStayTiers.map((x, j) =>
                                j === i ? { ...x, [k]: v } : x,
                              ),
                            }));
                          }}
                        />{' '}
                        %
                      </td>
                    ))}
                    <td>
                      <button
                        type="button"
                        className="pms-mini"
                        onClick={() =>
                          edit((p) => ({
                            ...p,
                            lengthOfStayTiers: p.lengthOfStayTiers.filter((_, j) => j !== i),
                          }))
                        }
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="pms-mini"
            onClick={() =>
              edit((p) => ({
                ...p,
                lengthOfStayTiers: [
                  ...p.lengthOfStayTiers,
                  { minNights: (p.lengthOfStayTiers.at(-1)?.minNights ?? 2) + 1, discountStay: 0, discountMeals: 0 },
                ],
              }))
            }
          >
            Ajouter un palier
          </button>

          <div className="pms-form-actions">
            <button
              type="button"
              className="auth-btn"
              disabled={busy}
              onClick={() =>
                void onRun('Profil enregistré.', () => pms.seasons.updateProfileDraft(selected.id, draft, null))
              }
            >
              Enregistrer
            </button>
            <button
              type="button"
              className="auth-btn"
              disabled={busy}
              onClick={async () => {
                if (
                  !window.confirm(
                    'Publier ce profil ?\n\nUn profil publié est FIGÉ : ses valeurs ne pourront plus changer. Une correction se fait en créant un nouveau profil.',
                  )
                )
                  return;
                // Save first: publishing freezes what is STORED, so publishing
                // with unsaved edits on screen would freeze the previous
                // contents while the operator is looking at the new ones.
                const saved = await onRun('', () =>
                  pms.seasons.updateProfileDraft(selected.id, draft, null),
                );
                if (!saved) return;
                await onRun('Profil publié.', () => pms.seasons.publishProfile(selected.id));
              }}
            >
              Publier
            </button>
            <button
              type="button"
              className="pms-mini"
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Supprimer ce brouillon ?')) return;
                void onRun('Brouillon supprimé.', () => pms.seasons.deleteProfileDraft(selected.id));
              }}
            >
              Supprimer
            </button>
          </div>
        </div>
      )}

      <GenerateGrid
        calendar={calendar}
        calendarId={calendarId}
        years={years}
        onSelectCalendar={onSelectCalendar}
        profiles={profiles}
        profileId={selected?.id ?? null}
        onSelectProfile={onSelect}
        canEdit={canEdit}
        busy={busy}
        onRun={onRun}
        pms={pms}
      />
    </>
  );
}

// ── Tarif du jour, and generating ────────────────────────────────────────────

function JourPane({
  calendar,
  profile,
  plans,
  roomTypes,
  calendarId,
  profileId,
  years,
  onSelectCalendar,
  profiles,
  onSelectProfile,
  canEdit,
  busy,
  onRun,
  pms,
}: {
  calendar: StoredSeasonCalendar | null;
  profile: RateProfile | null;
  plans: RatePlan[];
  roomTypes: RoomType[];
  calendarId: number | null;
  profileId: number | null;
  years: YearRow[];
  onSelectCalendar: (id: number | null) => void;
  profiles: StoredRateProfile[];
  onSelectProfile: (id: number | null) => void;
  canEdit: boolean;
  busy: boolean;
  onRun: Runner;
  pms: Pms;
}) {
  const today = useMemo(() => todayIsoParis(), []);
  const [date, setDate] = useState(today);
  const [roomTypeId, setRoomTypeId] = useState<number | null>(null);

  useEffect(() => {
    if (roomTypeId === null && roomTypes.length > 0) setRoomTypeId(roomTypes[0]!.id);
  }, [roomTypes, roomTypeId]);

  const roomType = roomTypes.find((r) => r.id === roomTypeId) ?? null;

  const matrix = useMemo(() => {
    if (calendar === null || profile === null || roomType === null) return null;
    return dayMatrix(
      { year: calendar.year, periods: calendar.periods, closures: calendar.closures },
      profile,
      plans,
      roomType,
      date,
    );
  }, [calendar, profile, plans, roomType, date]);

  if (calendar === null || profile === null) {
    return (
      <p className="pms-empty">
        Il faut un exercice ET un profil tarifaire pour calculer un tarif. Créez-les dans les deux premiers
        onglets.
      </p>
    );
  }

  const columns = matrix?.rows[0]?.cells.map((c) => c.label) ?? [];

  return (
    <>
      <div className="pms-toolbar">
        <label className="pms-field">
          <span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="pms-field">
          <span>Catégorie</span>
          <select value={roomTypeId ?? ''} onChange={(e) => setRoomTypeId(Number(e.target.value))}>
            {roomTypes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.labelFr}
              </option>
            ))}
          </select>
        </label>
      </div>

      {matrix?.period == null ? (
        <p className="pms-warn">Aucune période tarifaire ne couvre le {date}.</p>
      ) : (
        <>
          <p className="pms-sub">
            {matrix.period.description} — <strong>{RATE_SEASON_LABELS_FR[matrix.period.season]}</strong>, base{' '}
            {fmtCents(matrix.period.baseCents)} €{matrix.closed && ' · FERMÉ (arrêt de vente)'}
          </p>
          <div className="pms-table-wrap">
            <table className="pms-table">
              <thead>
                <tr>
                  <th>Formule</th>
                  <th>Nuit</th>
                  <th>Repas</th>
                  {columns.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.planId}>
                    <td>{row.planLabel}</td>
                    <td className="pms-num">{fmtCents(row.roomCents)}</td>
                    <td className="pms-num">{row.mealsCents === 0 ? '—' : fmtCents(row.mealsCents)}</td>
                    {row.cells.map((c) => (
                      <td key={c.label} className="pms-num">
                        {fmtCents(c.priceCents)}
                        {c.savingCents !== undefined && c.savingCents > 0 && (
                          <span className="pms-sub"> −{fmtCents(c.savingCents)}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pms-sub">
            Les colonnes OTA sont identiques, et c’est un fait mesuré : les tarifs ne varient pas par canal —
            ce sont les <em>restrictions</em> qui varient. Le direct diffère pour deux raisons indépendantes :
            la remise de durée et le tarif des repas en direct.
          </p>
        </>
      )}

      <GenerateGrid
        calendar={calendar}
        calendarId={calendarId}
        years={years}
        onSelectCalendar={onSelectCalendar}
        profiles={profiles}
        profileId={profileId}
        onSelectProfile={onSelectProfile}
        canEdit={canEdit}
        busy={busy}
        onRun={onRun}
        pms={pms}
      />
    </>
  );
}

// « Générer la grille » — shared by « Profil tarifaire » and « Tarif du jour », so
// the page always shows which calendar and profile the grid is built from.
function GenerateGrid({
  calendar,
  calendarId,
  years,
  onSelectCalendar,
  profiles,
  profileId,
  onSelectProfile,
  canEdit,
  busy,
  onRun,
  pms,
}: {
  calendar: StoredSeasonCalendar | null;
  calendarId: number | null;
  years: YearRow[];
  onSelectCalendar: (id: number | null) => void;
  profiles: StoredRateProfile[];
  profileId: number | null;
  onSelectProfile: (id: number | null) => void;
  canEdit: boolean;
  busy: boolean;
  onRun: Runner;
  pms: Pms;
}) {
  const today = useMemo(() => todayIsoParis(), []);
  const [genFrom, setGenFrom] = useState(today);
  const [genTo, setGenTo] = useState(today);
  const [report, setReport] = useState<GenerationReport | null>(null);

  if (!canEdit) return null;
  return (
    <>
          <h4>Générer la grille</h4>
          <p className="pms-sub">
            Écrit les tarifs dans « Inventaire » pour la période choisie. Les journées <strong>ajustées à la
            main</strong> ne sont jamais écrasées — elles sont comptées et signalées.
          </p>
          <p className="pms-hint">
            Grille construite à partir de : <strong>exercice {calendar?.year ?? '—'}</strong> ·{' '}
            <strong>
              profil {profiles.find((p) => p.id === profileId)?.parameters.name ?? 'aucun'}
            </strong>
          </p>
          <div className="pms-toolbar">
            <label className="pms-field">
              <span>Exercice</span>
              <select
                value={calendarId ?? ''}
                onChange={(e) => onSelectCalendar(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">— choisir —</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.year}
                  </option>
                ))}
              </select>
            </label>
            <label className="pms-field">
              <span>Profil tarifaire</span>
              <select
                value={profileId ?? ''}
                onChange={(e) => onSelectProfile(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">— choisir —</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.parameters.name} — {p.status}
                  </option>
                ))}
              </select>
            </label>
            <label className="pms-field">
              <span>Du</span>
              <input type="date" value={genFrom} onChange={(e) => setGenFrom(e.target.value)} />
            </label>
            <label className="pms-field">
              <span>Au</span>
              <input type="date" value={genTo} onChange={(e) => setGenTo(e.target.value)} />
            </label>
            <button
              type="button"
              className="auth-btn"
              disabled={busy || calendarId === null || profileId === null}
              onClick={async () => {
                if (calendarId === null || profileId === null) return;
                if (!window.confirm(`Générer les tarifs du ${genFrom} au ${genTo} ?`)) return;
                setReport(null);
                const r = await pms.seasons.generate({ calendarId, profileId, from: genFrom, to: genTo });
                if (!r.ok) {
                  await onRun('', () => Promise.resolve(r));
                  return;
                }
                setReport(r.value);
              }}
            >
              Générer
            </button>
            <button
              type="button"
              className="pms-mini"
              disabled={busy || calendar === null}
              onClick={() => {
                if (calendar === null) return;
                const { from, to } = fiscalYearRange(calendar.year);
                setGenFrom(from);
                setGenTo(to);
              }}
            >
              Tout l’exercice
            </button>
          </div>

          {report && (
            <div className={report.skipped > 0 ? 'pms-warn' : 'pms-hint'}>
              <strong>
                {report.written} tarif(s) écrit(s)
                {report.skipped > 0 && ` · ${report.skipped} conservé(s) — ajustés à la main`}
              </strong>
              {report.uncoveredDates.length > 0 && (
                <div>{report.uncoveredDates.length} jour(s) hors calendrier, non tarifé(s).</div>
              )}
              {report.closedDates.length > 0 && (
                <div>{report.closedDates.length} jour(s) en fermeture, écrits en arrêt de vente.</div>
              )}
              {report.unpricedRoomTypeIds.length > 0 && (
                <div>
                  {report.unpricedRoomTypeIds.length} catégorie(s) sans supplément saisi, donc non tarifée(s).
                </div>
              )}
            </div>
          )}
    </>
  );
}

// A saved profile is frozen, so it is shown read-only: the values the operator
// published are the ones the grid was built from, and they must be visible.
function ProfileReadOnly({ parameters, roomTypes }: { parameters: RateProfile; roomTypes: RoomType[] }) {
  return (
    <div className="pms-form">
      <h4>Suppléments de catégorie</h4>
      <div className="pms-table-wrap">
        <table className="pms-table">
          <thead>
            <tr>
              <th>Catégorie</th>
              {RATE_SEASONS.map((s) => (
                <th key={s}>{RATE_SEASON_LABELS_FR[s]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roomTypes.map((rt) => {
              const r = parameters.roomTypes[rt.id];
              return (
                <tr key={rt.id}>
                  <td>{rt.labelFr}</td>
                  {RATE_SEASONS.map((s) => (
                    <td key={s} className="pms-num">
                      {r ? fmtCents(r.markup[s]) : '—'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4>Séjour minimum par saison</h4>
      <p className="pms-sub">
        {RATE_SEASONS.map((s) => `${RATE_SEASON_LABELS_FR[s]} : ${parameters.minStayBySeason[s]} nuit(s)`).join(' · ')}
      </p>

      <h4>Repas</h4>
      <p className="pms-sub">
        OTA — petit-déjeuner {fmtCents(parameters.mealsBooking.breakfast)} € · dîner{' '}
        {fmtCents(parameters.mealsBooking.dinner)} €. Direct — petit-déjeuner {fmtCents(parameters.mealsDirect.breakfast)} €
        · dîner {fmtCents(parameters.mealsDirect.dinner)} €.
      </p>

      <h4>Repas enfants — direct</h4>
      <p className="pms-sub">
        {parameters.childMealsDirect == null
          ? 'Non définis : un enfant paie ses repas au prix adulte direct.'
          : CHILD_MEAL_BANDS.map(
              (b) =>
                `${CHILD_MEAL_BAND_LABELS_FR[b]} — petit-déjeuner ${fmtCents(parameters.childMealsDirect![b].breakfast)} € · dîner ${fmtCents(parameters.childMealsDirect![b].dinner)} €`,
            ).join(' · ')}
      </p>

      <h4>Remises de séjour</h4>
      <p className="pms-sub">
        {parameters.lengthOfStayTiers.length === 0
          ? 'Aucune.'
          : parameters.lengthOfStayTiers
              .map((t) => `à partir de ${t.minNights} nuit(s) : −${fmtPct(t.discountStay)} % sur le séjour`)
              .join(' · ')}
      </p>
    </div>
  );
}
